import type {
  AdminIapStatusFilter,
  AdminIapTransactionsResponse,
  AdminMatchmakingQueueResponse,
} from '@shared/types';
import { api } from './api';

/**
 * Clients for the dashboard's drill-down endpoints — the detail behind a
 * metric card the operator clicked.
 *
 *   - GET /admin/matchmaking/queue      → who is waiting, and for how long
 *   - GET /admin/iap/transactions       → the purchase rows behind the IAP cards
 *
 * Both are viewer-tier reads, same as `/admin/dashboard/metrics` itself.
 */

/** Build a query string, skipping empty / undefined values. */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function getMatchmakingQueue(): Promise<AdminMatchmakingQueueResponse> {
  return api<AdminMatchmakingQueueResponse>('/admin/matchmaking/queue');
}

export interface ListIapTransactionsParams {
  page?: number;
  limit?: number;
  // The SHARED union, never a hand-copied one: a local copy silently drops
  // whatever the server later adds (it dropped `'live'`, which is exactly the
  // scope the dashboard's Transactions card links with).
  status?: AdminIapStatusFilter;
  environment?: 'production' | 'sandbox' | 'unknown';
  platform?: 'apple' | 'google';
}

export async function listIapTransactions(
  params: ListIapTransactionsParams = {},
): Promise<AdminIapTransactionsResponse> {
  const qs = toQuery({
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    status: params.status,
    environment: params.environment,
    platform: params.platform,
  });
  return api<AdminIapTransactionsResponse>(`/admin/iap/transactions${qs}`);
}
